import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar } from '@talk2nick/ui';

import type { AuthState } from '../features/auth/useAuth';
import { useConvoList } from '../features/chat/useChat';

import { useUserProfiles } from '../features/chat/useUserProfiles';
import {
  clearShareIntake,
  readShareIntake,
  shareTextOf,
  type ShareIntake,
} from '../features/chat/shareIntake';
import { rememberConvoTtl, sendText } from '../features/chat/messages';
import { sendMedia } from '../features/chat/mediaUpload';
import { prepareAudio, prepareFile, prepareImage, prepareVideo } from '../features/chat/media';
import { useConvo } from '../features/chat/useChat';

/**
 * 공유 수신 라우트 (요구사항 6, PLAN 5-6장, M6).
 *
 * 흐름 (Android):
 *   1. 다른 앱에서 "공유" → PWA 선택 → 매니페스트의 share_target이 POST /share
 *   2. 서비스워커(apps/web/src/sw/share-target.ts)가 POST를 가로채 Cache Storage에 저장
 *      → 303으로 `/share?token=...` 리다이렉트
 *   3. 이 컴포넌트가 캐시를 읽어 **보낼 대화방을 고르고** 전송한다
 *
 * iOS Safari는 share_target 미지원 → 앱 안에서 "붙여넣기/공유"로 URL·텍스트만 온다.
 * 그래서 링크가 오면 같은 화면에서 그대로 보낼 수 있게 했다.
 */

/** 전송할 대화방 후보 수 — 목록 전체를 그릴 필요는 없다 */
const CONVO_LIMIT = 20;

export default function ShareRoute({ auth }: { auth: AuthState }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const myUid = auth.firebaseUser?.uid ?? null;
  const { items } = useConvoList(myUid);

  const [intake, setIntake] = useState<ShareIntake | null>(null);
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = params.get('token');

  // 꼬리표: URL이 share_target 없이 직접 들어온 경우(공유扩展/북마크)
  const manualText = useMemo(
    () => [params.get('text'), params.get('title'), params.get('url')].filter(Boolean).join('\n'),
    [params],
  );

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    void readShareIntake(token)
      .then((result) => {
        if (cancelled) return;
        setIntake(result);
        if (!result) setError(t('error.notFound'));
      })
      .catch((err: unknown) => {
        console.error('[talk2nIcK] 공유 데이터 로드 실패:', err);
        if (!cancelled) setError(t('error.generic'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  const body = intake ? shareTextOf(intake.meta) : manualText;
  const files = intake?.files ?? [];

  // 전송 경로(messages/mediaUpload)는 방별 TTL을 모듈 캐시에서 읽는다.
  // 공유 화면은 방 문서를 안 읽고 있으므로, 선택된 방의 TTL만 직접 내려놓는다.
  const targetConvo = useConvo(target);
  useEffect(() => {
    if (!target) return;
    rememberConvoTtl(target, targetConvo?.settings?.disappearingTtlSeconds ?? 0);
  }, [target, targetConvo?.settings?.disappearingTtlSeconds]);

  const convos = useMemo(
    () =>
      items
        .slice()
        .sort((a, b) => {
          const at = (c: typeof a) => c.convo.lastMessage?.createdAt?.toDate().getTime() ?? 0;
          return at(b) - at(a);
        })
        .slice(0, CONVO_LIMIT),
    [items],
  );
  const peerUids = useMemo(
    () => [
      ...new Set(
        convos
          .map(({ convo }) => convo.memberIds.find((uid) => uid !== myUid))
          .filter((uid): uid is string => uid !== undefined),
      ),
    ],
    [convos, myUid],
  );
  const profiles = useUserProfiles(peerUids);

  async function onSend() {
    if (!myUid || !target || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (body) await sendText(target, myUid, body);
      for (const file of files) {
        const prepared = await prepareShared(file);
        await sendMedia(target, myUid, prepared);
      }
      if (token) await clearShareIntake(token);
      navigate(`/chat/${target}`, { replace: true });
    } catch (err) {
      console.error('[talk2nIcK] 공유 전송 실패:', err);
      setError(t('error.generic'));
      setBusy(false);
    }
  }

  if (auth.status !== 'authenticated') {
    return (
      <Centered>
        <p className="text-sm text-[var(--text-secondary)]">{t('common.loading')}</p>
      </Centered>
    );
  }

  if (loading) {
    return (
      <Centered>
        <p className="text-sm text-[var(--text-secondary)]">{t('common.loading')}</p>
      </Centered>
    );
  }

  if (error && !body && files.length === 0) {
    return (
      <Centered>
        <p className="text-sm text-[var(--danger)]">{error}</p>
        <button
          type="button"
          onClick={() => navigate('/chats')}
          className="pressable rounded-[var(--radius-md)] bg-[var(--accent)] px-4 py-2 text-sm text-white"
        >
          {t('common.back')}
        </button>
      </Centered>
    );
  }

  return (
    <>
      <header className="appbar">
        <button
          type="button"
          onClick={() => navigate('/chats')}
          className="tap-target pressable"
          aria-label={t('common.back')}
        >
          ←
        </button>
        <h1 className="appbar__title">{t('share.title')}</h1>
      </header>

      <div className="flex min-h-0 flex-1 flex-col bg-[var(--bg-list)]">
        {/* 보낼 내용 미리보기 — 무엇이 붙어 들어왔는지 숨기지 않는다 */}
        <div className="border-b border-[var(--divider)] px-4 py-3">
          {body && (
            <p className="max-h-24 overflow-y-auto rounded-[var(--radius-md)] bg-[var(--bubble-in)] p-3 text-sm whitespace-pre-wrap">
              {body}
            </p>
          )}
          {files.length > 0 && (
            <p className="mt-1 text-xs text-[var(--text-secondary)]">
              {t('share.files', { count: files.length })}
            </p>
          )}
        </div>

        <ul className="scroll-area min-h-0 flex-1">
          {convos.length === 0 ? (
            <li>
              <p className="px-4 py-3 text-sm text-[var(--text-secondary)]">{t('chat.noConvos')}</p>
            </li>
          ) : (
            convos.map(({ id, convo }) => {
              const peerUid = convo.memberIds.find((uid) => uid !== myUid);
              const peer = peerUid ? profiles.get(peerUid) : undefined;
              const title = convo.title ?? peer?.displayName ?? (peer ? `@${peer.handle}` : '…');
              return (
                <li key={id}>
                  <button
                    type="button"
                    onClick={() => setTarget(id)}
                    className={`pressable flex w-full items-center gap-3 px-4 py-3 text-left ${
                      target === id ? 'bg-[var(--accent)]/10' : ''
                    }`}
                  >
                    <Avatar src={peer?.photoURL} name={title} size={40} />
                    <span className="min-w-0 flex-1 truncate text-base">{title}</span>
                    {target === id && (
                      <span aria-hidden className="text-[var(--accent)]">
                        ✓
                      </span>
                    )}
                  </button>
                </li>
              );
            })
          )}
        </ul>

        {error && <p className="px-4 py-2 text-xs text-[var(--danger)]">{error}</p>}

        <div className="border-t border-[var(--divider)] px-4 py-3 pb-[var(--safe-bottom)]">
          <button
            type="button"
            disabled={!target || busy}
            onClick={() => void onSend()}
            className="pressable w-full rounded-[var(--radius-md)] bg-[var(--accent)] px-4 py-3 font-medium text-white disabled:opacity-40"
          >
            {busy ? t('common.loading') : t('share.send')}
          </button>
        </div>
      </div>
    </>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
      {children}
    </div>
  );
}

/** 공유로 들어온 파일을 전송 파이프라인에 맞는 형태로 전처리한다 */
async function prepareShared(file: File) {
  if (file.type.startsWith('image/')) return prepareImage(file, file.name);
  if (file.type.startsWith('video/')) return prepareVideo(file);
  if (file.type.startsWith('audio/')) return prepareAudio(file, 0, file.name);
  return prepareFile(file);
}
